import React from 'react';
import { Container, Typography, Box, Button, Divider, Grid } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link } from 'react-router-dom';

const C = {
  purple:    '#552583',
  gold:      '#FDB927',
  bg:        '#E4F0F6',
  white:     '#ffffff',
  text:      '#1a1a1a',
  textSoft:  '#4a4a4a',
  textMuted: '#7a7a7a',
};

const FAQ_ITEMS = [
  {
    q: 'Comment passer une commande ?',
    a: 'Choisissez un restaurant, ajoutez vos plats au panier et confirmez votre commande. Vous recevrez une confirmation et pourrez suivre votre livraison.',
  },
  {
    q: 'Quels sont les moyens de paiement acceptés ?',
    a: 'Nous acceptons le paiement en espèces à la livraison et par carte bancaire via notre système sécurisé.',
  },
  {
    q: 'Comment annuler une commande ?',
    a: 'Contactez-nous rapidement par WhatsApp ou email. Une commande déjà en préparation ne peut pas être annulée.',
  },
  {
    q: 'Mon restaurant préféré n\'est pas sur Usafiri, que faire ?',
    a: 'Envoyez-nous un message ! Nous contactons les restaurants régulièrement pour agrandir notre liste de partenaires.',
  },
  {
    q: 'Comment devenir livreur ou partenaire ?',
    a: 'Rendez-vous sur la page "Devenir partenaire" depuis le menu principal et remplissez le formulaire. Notre équipe vous recontactera sous 48h.',
  },
];

export default function Aide() {
  return (
    <Box sx={{ backgroundColor: C.bg, minHeight: '100vh' }}>

      {/* Hero */}
      <Box sx={{ backgroundColor: C.purple, py: { xs: 6, md: 8 }, textAlign: 'center' }}>
        <Container maxWidth="md">
          <HelpOutlineIcon sx={{ fontSize: 48, color: C.gold, mb: 2 }} />
          <Typography variant="h3" fontWeight={800} color="#fff" sx={{ mb: 1.5, fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
            Comment pouvons-nous vous aider ?
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
            Notre équipe est disponible pour répondre à toutes vos questions.
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: 6 }}>

        {/* Contact */}
        <Grid container spacing={3} sx={{ mb: 6 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{
              backgroundColor: C.white, borderRadius: '16px',
              p: 3, textAlign: 'center',
              border: '1px solid #e8e8e8',
              '&:hover': { boxShadow: '0 4px 20px rgba(85,37,131,0.1)' },
              transition: 'box-shadow 0.2s',
            }}>
              <WhatsAppIcon sx={{ fontSize: 36, color: '#25D366', mb: 1.5 }} />
              <Typography fontWeight={700} sx={{ color: C.text, mb: 0.5 }}>WhatsApp</Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', mb: 2 }}>
                Réponse rapide, 7j/7
              </Typography>
              <Button
                variant="contained"
                href="https://wa.me/262639000000"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: '#25D366', color: '#fff', borderRadius: '10px',
                  textTransform: 'none', fontWeight: 700, px: 3,
                  '&:hover': { backgroundColor: '#1ebe57' },
                }}
              >
                Nous contacter
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{
              backgroundColor: C.white, borderRadius: '16px',
              p: 3, textAlign: 'center',
              border: '1px solid #e8e8e8',
              '&:hover': { boxShadow: '0 4px 20px rgba(85,37,131,0.1)' },
              transition: 'box-shadow 0.2s',
            }}>
              <EmailIcon sx={{ fontSize: 36, color: C.purple, mb: 1.5 }} />
              <Typography fontWeight={700} sx={{ color: C.text, mb: 0.5 }}>Email</Typography>
              <Typography sx={{ color: C.textMuted, fontSize: '0.85rem', mb: 2 }}>
                Réponse sous 24h
              </Typography>
              <Button
                variant="outlined"
                href="mailto:contact@usafiri.yt"
                sx={{
                  borderColor: C.purple, color: C.purple, borderRadius: '10px',
                  textTransform: 'none', fontWeight: 700, px: 3,
                  '&:hover': { backgroundColor: `${C.purple}10` },
                }}
              >
                contact@usafiri.yt
              </Button>
            </Box>
          </Grid>
        </Grid>

        {/* Horaires */}
        <Box sx={{
          backgroundColor: '#FFF8E7', border: '1px solid #FDB927',
          borderRadius: '12px', p: 2.5, mb: 6,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <AccessTimeIcon sx={{ color: '#D97706' }} />
          <Typography sx={{ color: '#92400E', fontSize: '0.9rem' }}>
            <strong>Horaires d'assistance :</strong> Lundi – Samedi, 8h00 – 20h00 (heure de Mayotte)
          </Typography>
        </Box>

        {/* FAQ */}
        <Typography variant="h5" fontWeight={800} sx={{ color: C.text, mb: 3 }}>
          Questions fréquentes
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 6 }}>
          {FAQ_ITEMS.map((item, i) => (
            <Box key={i} sx={{
              backgroundColor: C.white, borderRadius: '12px',
              border: '1px solid #e8e8e8', p: 3,
            }}>
              <Typography fontWeight={700} sx={{ color: C.text, mb: 1, fontSize: '0.95rem' }}>
                {item.q}
              </Typography>
              <Typography sx={{ color: C.textSoft, fontSize: '0.88rem', lineHeight: 1.6 }}>
                {item.a}
              </Typography>
            </Box>
          ))}
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography sx={{ color: C.textMuted, fontSize: '0.9rem', mb: 2 }}>
            Vous avez d'autres questions ?
          </Typography>
          <Button
            component={Link}
            to="/faq"
            variant="outlined"
            sx={{
              borderColor: C.purple, color: C.purple, borderRadius: '10px',
              textTransform: 'none', fontWeight: 700, px: 4,
            }}
          >
            Voir toutes les FAQ
          </Button>
        </Box>

      </Container>
    </Box>
  );
}
